/**
 * @module pages/ThankYouPage
 * @description Brief §4.8 branded thank-you: confirmation, transaction
 * summary, receipt information, share your impact, continue browsing.
 */
import { useEffect, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { navigate } from "../app/router";
import { Icon } from "../components/Icons";
import type { CartLine, GiftReceipt } from "../domain/types";
import { formatUsd, plural } from "../lib/format";
import "./Checkout.css";

/** Social share targets: plain share URLs, no tracking scripts. */
function shareLinks(text: string, url: string): { label: string; href: string }[] {
    const t: string = encodeURIComponent(text);
    const u: string = encodeURIComponent(url);
    return [
        { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
        { label: "X", href: `https://twitter.com/intent/tweet?text=${t}&url=${u}` },
        { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
        { label: "WhatsApp", href: `https://wa.me/?text=${t}%20${u}` },
    ];
}

/** Thank-you page. */
export function ThankYouPage(): JSX.Element {
    const { receipt, causeById } = useGiving();
    // No receipt (direct visit or reload): back to browsing, never a blank page.
    useEffect(() => {
        if (receipt === null) navigate("browse");
    }, [receipt]);
    if (receipt === null) return <main className="container" />;
    const r: GiftReceipt = receipt;
    const pageUrl: string = `${window.location.origin}${window.location.pathname}`;
    const shareText: string = `I just gave to ${plural(r.lines.length, "cause")} in Jamaica through The American Friends of Jamaica. Choose yours:`;

    return (
        <main className="thanks">
            <section className="thanks__hero">
                <img src="causes/40-years-of-giving.jpg" alt="" className="thanks__bg" />
                <div className="container thanks__hero-inner">
                    <span className="thanks__check"><Icon name="check" size={34} /></span>
                    <p className="kicker thanks__kicker">Thank you{r.donor.firstName ? `, ${r.donor.firstName}` : ""}</p>
                    <h1 className="thanks__title">Your love just landed in Jamaica.</h1>
                    <p>Your gift of <strong>{formatUsd(r.totalUsd)}</strong> supports {plural(r.lines.length, "cause")}.</p>
                </div>
            </section>

            <div className="container thanks__grid">
                <section className="thanks__card" aria-labelledby="txn-title">
                    <h2 id="txn-title" className="checkout__h2">Transaction summary</h2>
                    <dl className="thanks__meta">
                        <div><dt>Reference</dt><dd>{r.reference} <span className="badge badge--sample">demo</span></dd></div>
                        <div><dt>Date</dt><dd>{new Date(r.completedAt).toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" })}</dd></div>
                        <div><dt>Receipt</dt><dd>Emailed to {r.donor.email} by AFJ through Neon (live site only)</dd></div>
                    </dl>
                    <ul className="thanks__lines">
                        {r.lines.map((l: CartLine) => (
                            <li key={l.causeId}><span>{causeById(l.causeId)?.title ?? l.causeId}</span><strong>{formatUsd(l.amountUsd)}</strong></li>
                        ))}
                        <li className="thanks__lines-total"><span>Total</span><strong>{formatUsd(r.totalUsd)}</strong></li>
                    </ul>
                    <p className="checkout__fine">A tax-deductible gift to The American Friends of Jamaica, Inc., a 501(c)(3), designated to the causes above.</p>
                </section>

                <section className="thanks__card" aria-labelledby="share-title">
                    <h2 id="share-title" className="checkout__h2">Share your impact</h2>
                    <p>Invite friends to choose a cause of their own.</p>
                    <div className="thanks__share">
                        {shareLinks(shareText, pageUrl).map((s) => (
                            <a key={s.label} className="btn btn--ghost" href={s.href} target="_blank" rel="noopener noreferrer">{s.label}</a>
                        ))}
                    </div>
                    <button type="button" className="btn btn--primary btn--lg btn--block" onClick={() => navigate("browse")}>
                        Continue browsing causes <Icon name="arrow-right" />
                    </button>
                </section>
            </div>
        </main>
    );
}
